/**
 * Carte, et carte de graphique.
 *
 * `ChartCard` ajoute deux choses que le graphique seul ne fournit pas :
 *   · un état vide explicite, plutôt qu'un cadre d'axes sans données ;
 *   · une vue tableau repliable.
 *
 * La vue tableau n'est pas un bonus : c'est le canal qui rend chaque valeur
 * atteignable sans survol — au clavier, au lecteur d'écran, à la copie vers un
 * tableur. Les infobulles enrichissent, elles ne doivent jamais conditionner
 * l'accès à un chiffre.
 */

import type { CSSProperties, ReactNode } from 'react';

import type { DataColumn } from '../types';

interface CardProps {
  title?: string;
  subtitle?: string;
  className?: string;
  /**
   * Échappatoire pour les couleurs de STATUT.
   *
   * Les teintes de `STATUS_COLORS` / `STATUS_TINTS` vivent dans `theme.ts` et
   * n'ont pas de jumelles en CSS — c'est le choix du projet depuis `AlertList`,
   * qui les pose déjà en style inline. Les redéclarer en classes ferait un
   * troisième jeu de valeurs à tenir synchronisé.
   *
   * Réservé à ça : tout ce qui est mise en page passe par `className`.
   */
  style?: CSSProperties;
  children: ReactNode;
}

export function Card({ title, subtitle, className, style, children }: CardProps) {
  return (
    <section className={className ? `card ${className}` : 'card'} style={style}>
      {(title || subtitle) && (
        <header className="card__head">
          {title && <h2 className="card__title">{title}</h2>}
          {subtitle && <p className="card__subtitle">{subtitle}</p>}
        </header>
      )}
      {children}
    </section>
  );
}

interface ChartCardProps<T> {
  title: string;
  subtitle?: string;
  className?: string;
  /** Message affiché à la place du graphique quand il n'y a rien à tracer. */
  emptyMessage?: string;
  /**
   * Hauteur de la zone de tracé, en pixels.
   *
   * La valeur par défaut (260 px, dans `.chart-area`) convient aux séries
   * temporelles. Un graphique à barres horizontales, lui, grandit avec le
   * nombre de catégories : sans cette échappatoire il déborderait de sa carte
   * et passerait par-dessus le lien « Voir les données ».
   *
   * Toujours une hauteur EXPLICITE : le ResponsiveContainer de Recharts mesure
   * son parent, et un parent sans hauteur définie lui fait calculer zéro.
   */
  chartHeight?: number;
  rows: readonly T[];
  columns: ReadonlyArray<DataColumn<T>>;
  /**
   * Rendu entre la zone de tracé et la vue tableau.
   *
   * Sert à la bande de marqueurs : les traits posés sur le graphique ne
   * portent qu'un numéro, et ce qu'ils désignent doit être écrit juste en
   * dessous — donc dans la carte, pas dans la page.
   */
  footer?: ReactNode;
  children: ReactNode;
}

export function ChartCard<T>({
  title,
  subtitle,
  className,
  emptyMessage = 'Aucune donnée sur cette période.',
  chartHeight,
  rows,
  columns,
  footer,
  children,
}: ChartCardProps<T>) {
  const hasData = rows.length > 0;

  return (
    <Card title={title} subtitle={subtitle} className={className}>
      <div className="chart-area" style={chartHeight ? { height: chartHeight } : undefined}>
        {hasData ? children : <p className="empty">{emptyMessage}</p>}
      </div>

      {hasData && footer}

      {hasData && (
        <details className="data-toggle">
          <summary>Voir les données</summary>
          <div className="data-toggle__body table-wrap">
            <table className="data">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.key} className={column.numeric ? 'num' : undefined}>
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    {columns.map((column) => (
                      <td key={column.key} className={column.numeric ? 'num' : undefined}>
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </Card>
  );
}
